import {getLogger} from "../../../Services/LoggingService";
import {
    BaseWidgetConfiguration,
    BaseWidgetConfigurationInput,
    CALENDAR_SHOW_PER_DAY,
    CalendarCellAdditionalInfo,
} from "../../../Contracts/WidgetTypes/BaseWidget";

type DisplaySettings = BaseWidgetConfiguration['displaySettings'];
type DisplaySettingsInput = BaseWidgetConfigurationInput['displaySettings'];

// #region DefaultDisplaySettings
/**
 * Every display option is off, so a vendor who has set nothing keeps the rendering they
 * already have. These values must never change.
 */
export const defaultDisplaySettings = {
    appointmentList: {
        showCapacity: false,
        showPrices: false,
    },
    calendar: {
        showPerDay: false,
    },
} as const satisfies DisplaySettings;
// #endregion DefaultDisplaySettings

const reportRejected = (property: string, value: unknown): void => {
    getLogger().logEvent('displaySettings.invalidValue', {property, value});
};

const asBoolean = (value: unknown, property: string, fallback: boolean): boolean => {
    if (value === undefined) {
        return fallback;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    reportRejected(property, value);
    return fallback;
};

const asShowPerDay = (value: unknown, property: string, fallback: CalendarCellAdditionalInfo): CalendarCellAdditionalInfo => {
    if (value === undefined) {
        return fallback;
    }
    if (CALENDAR_SHOW_PER_DAY.includes(value as CalendarCellAdditionalInfo)) {
        return value as CalendarCellAdditionalInfo;
    }
    reportRejected(property, value);
    return fallback;
};

/**
 * Resolves the `displaySettings` configuration.
 *
 * Each property is defaulted independently, so a partially specified object such as
 * `{calendar: {}}` still yields the documented default at every other level. Values
 * outside the published vocabulary are reported and replaced by their default, because
 * the configuration arrives from untrusted sources (a remote config blob and an HTML
 * data attribute) that the compile-time contract cannot constrain.
 *
 * @param displaySettings the display settings provided in the input configuration, if any
 * @returns a fully hydrated displaySettings object, sharing no references with the input or the defaults
 */
export const applyDisplaySettings = (displaySettings?: DisplaySettingsInput): DisplaySettings => ({
    appointmentList: {
        showCapacity: asBoolean(
            displaySettings?.appointmentList?.showCapacity,
            'appointmentList.showCapacity',
            defaultDisplaySettings.appointmentList.showCapacity,
        ),
        showPrices: asBoolean(
            displaySettings?.appointmentList?.showPrices,
            'appointmentList.showPrices',
            defaultDisplaySettings.appointmentList.showPrices,
        ),
    },
    calendar: {
        showPerDay: asShowPerDay(
            displaySettings?.calendar?.showPerDay,
            'calendar.showPerDay',
            defaultDisplaySettings.calendar.showPerDay,
        ),
    },
});
